import { useMemo, useState } from 'react';

import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import { IMPORT_STATE } from '../types';
import SalesforceImportConversationsModal from './SalesforceImportConversationsModal';
import SalesforceOrganizationConnectionModal from './SalesforceOrganizationConnectionModal';
import { useSalesforce } from './useSalesforce';

type ConnectionMode = 'create' | 'update';

export const SalesforceItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const {
    salesforceOrganizations,
    connectSalesforceMutation,
    updateSalesforceMutation,
    disconnectSalesforceMutation,
    syncSalesforceMutation,
    updateAutoSyncMutation,
    requestImportMutation,
    getSalesforceConnectError,
  } = useSalesforce(!isConnector);

  const [connectionTargetUserId, setConnectionTargetUserId] = useState<
    string | null
  >(null);
  const [connectionMode, setConnectionMode] =
    useState<ConnectionMode>('create');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [importAlias, setImportAlias] = useState<string | null>(null);

  const importOrganization = useMemo(() => {
    return salesforceOrganizations.find((item) => item.alias === importAlias);
  }, [importAlias, salesforceOrganizations]);

  const openCreateModal = () => {
    setConnectionError(null);
    setConnectionMode('create');
    setConnectionTargetUserId('');
  };

  const openUpdateModal = (userId: string) => {
    setConnectionError(null);
    setConnectionMode('update');
    setConnectionTargetUserId(userId);
  };

  const handleDisconnect = (salesforceAlias: string) => {
    disconnectSalesforceMutation(salesforceAlias);
  };

  const handleConnectionSubmit = async ({
    userId,
    password,
    consumerKey,
    consumerSecret,
    mode,
  }: {
    userId: string;
    password: string;
    consumerKey: string;
    consumerSecret: string;
    mode: 'create' | 'update';
  }): Promise<boolean> => {
    if (!currWorkspace) return false;

    try {
      if (mode === 'create') {
        const created = await connectSalesforceMutation({
          userId,
          password,
          consumerKey,
          consumerSecret,
        });
        const alias = created.alias;
        if (alias) {
          setImportAlias(alias);
        }
      } else {
        await updateSalesforceMutation({
          userId,
          password,
          consumerKey,
          consumerSecret,
        });
      }

      setConnectionTargetUserId(null);
      setConnectionError(null);
      return true;
    } catch (e) {
      setConnectionError(getSalesforceConnectError(e));
      return false;
    }
  };

  const isConnectionModalOpen = connectionTargetUserId !== null;

  return (
    <>
      <ProviderContainer
        source={SOURCE_TYPE.SALESFORCE}
        connected={salesforceOrganizations.length > 0}
        multiInfoEnabled={true}
        infos={salesforceOrganizations}
        description="Automate pushing user feedback to Syncly."
        connectedDisplay={(info) => info.user_id}
        onConnectClick={openCreateModal}
        onAddInfoClick={openCreateModal}
        onDisconnect={(info) => handleDisconnect(info.alias)}
        additionalActions={(info) =>
          info.import_state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <div className="flex gap-2">
              {info.import_state === IMPORT_STATE.NOT_STARTED ? (
                <Button
                  variant="outlined"
                  onClick={() => setImportAlias(info.alias)}
                >
                  Complete integration
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => syncSalesforceMutation(info.alias)}
                  >
                    One-time sync
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      updateAutoSyncMutation({
                        salesforceAlias: info.alias,
                        sync: !info.auto_sync,
                      })
                    }
                  >
                    {info.auto_sync
                      ? 'Stop periodic sync'
                      : 'Start periodic sync'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setImportAlias(info.alias)}
                  >
                    Import options
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => openUpdateModal(info.user_id)}
                  >
                    Update
                  </Button>
                </>
              )}
            </div>
          )
        }
      />

      <Modal
        status={isConnectionModalOpen}
        closeModal={() => setConnectionTargetUserId(null)}
        useBackgroundClose
      >
        <SalesforceOrganizationConnectionModal
          userId={connectionMode === 'create' ? null : connectionTargetUserId}
          error={connectionError}
          onSubmit={handleConnectionSubmit}
        />
      </Modal>

      <Modal
        status={!!importAlias}
        closeModal={() => setImportAlias(null)}
        useBackgroundClose
      >
        {!!importOrganization && (
          <SalesforceImportConversationsModal
            organization={importOrganization}
            onImport={async ({
              accountSync,
              taskSync,
              startDate,
              endDate,
              syncConditionInfo,
            }) => {
              await requestImportMutation({
                salesforceAlias: importOrganization.alias,
                accountSync,
                taskSync,
                startDate,
                endDate,
                syncConditionInfo,
              });
            }}
          />
        )}
      </Modal>
    </>
  );
};
